import {useEffect, useState} from 'react';
import type {List} from 'immutable';

import type {ReleaseStudentCounts} from '../AssignmentsList.helpers';
import {getReleaseStudentCounts} from '../AssignmentsList.helpers';
import {getTeacherAssignmentClassroomsQuery} from '../assignmentClassrooms.queries';
import {useAssignmentReleaseScopeQuery} from '../queries/assignmentReleaseScope.queries.generated';

interface Args {
  assignmentId: string;
  asEducatorId?: string;
  /** Only fetch while the release modal is open. */
  skip: boolean;
}

interface ReleaseScope {
  /** Who a cross-class release would reach, for the modal's "All classes" option. */
  allClassesStudentCounts?: ReleaseStudentCounts;
  /** Names of the classes a release would reach, alphabetically. */
  classroomNames: string[];
  /** Whether to offer the choice at all. */
  spansMultipleClasses: boolean;
  /** Not yet known — callers must not let a release be confirmed on a false `false` above. */
  isScopeLoading: boolean;
  hasScopeError: boolean;
}

interface NamedClassroom {
  getName: () => string;
}

interface ClassroomsState {
  names: string[];
  hasError: boolean;
}

const NO_CLASSROOM_NAMES: string[] = [];

export const useAssignmentReleaseScope = ({
  assignmentId,
  asEducatorId,
  skip
}: Args): ReleaseScope => {
  const [classrooms, setClassrooms] = useState<ClassroomsState | null>(null);

  const {data, loading, error} = useAssignmentReleaseScopeQuery({
    variables: {assignmentId, asEducatorId},
    // Counts move as the teacher grades, so a reopened modal must not show the first answer.
    fetchPolicy: 'cache-and-network',
    skip
  });

  useEffect(() => {
    if (skip) {
      return;
    }
    let subscribed = true;
    getTeacherAssignmentClassroomsQuery(assignmentId)
      .getResourcePromise()
      .then((fetched) => {
        if (subscribed) {
          setClassrooms({
            names: (fetched as List<NamedClassroom>)
              .toArray()
              .map((classroom) => classroom.getName()),
            hasError: false
          });
        }
      })
      .catch(() => {
        if (subscribed) {
          setClassrooms({names: [], hasError: true});
        }
      });
    return () => {
      subscribed = false;
    };
  }, [assignmentId, skip]);

  const classroomNames = classrooms?.names ?? NO_CLASSROOM_NAMES;
  const isScopeLoading = !skip && (loading || classrooms === null);
  const hasScopeError = Boolean(error) || Boolean(classrooms?.hasError);

  const assignment = data?.assignmentsAsEducator?.results?.[0];
  if (!assignment) {
    return {classroomNames, spansMultipleClasses: false, isScopeLoading, hasScopeError};
  }

  return {
    allClassesStudentCounts: getReleaseStudentCounts(assignment),
    classroomNames,
    spansMultipleClasses: classroomNames.length > 1,
    isScopeLoading,
    hasScopeError
  };
};
